import { useSpaceParameterOpenClause } from '@src/components/AppLayout/SpaceParameters';
import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CUSTOMER_TRANSACTION,
  DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  Toggle,
  agGridMenuSeparator,
  filterByCellValueMenuItem,
  filterExistsAndExcludes,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  useWSFilterPipe,
  useWsBlotterTable,
  type CustomerTransaction,
  type WebsocketRequest,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, RowDoubleClickedEvent, StateUpdatedEvent } from 'ag-grid-community';
import { pick } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useTradingSettings } from '../../../providers/AppConfigProvider';
import { defaultCustomerTransactionColumns, defaultCustomerTransactionFilter } from './defaults';
import type { CustomerTransactionsFilter, CustomerTransactionsTabsProps } from './types';
import { useCustomerTransactionColumns } from './useCustomerTransactionColumns';
import {
  CUSTOMER_TRANSACTIONS_SPACE_PARAMETER_SUPPORT,
  colIDToFilterBuilderKey,
  useCustomerTransactionsFilter,
} from './useCustomerTransactionsFilter';

function getCustomerTransactionKey(ct: CustomerTransaction) {
  return ct.TransactionID;
}

interface CustomerTransactionsBlotterProps {
  blotterID: string;
  tab: CustomerTransactionsTabsProps;
  onUpdateTab: (tab: CustomerTransactionsTabsProps) => void;
  initialFiltersOpen?: boolean;
  onCloneTab: () => void;
  onRowDoubleClicked: (event: RowDoubleClickedEvent<CustomerTransaction>) => void;
}

export const CustomerTransactionsBlotter = ({
  blotterID,
  tab,
  initialFiltersOpen,
  onCloneTab,
  onRowDoubleClicked,
  onUpdateTab,
}: CustomerTransactionsBlotterProps) => {
  const mixpanel = useMixpanel();
  const { showAllDecimalsInBlotters } = useTradingSettings();
  const defaultBlotterColumns = tab.defaultColumns ?? defaultCustomerTransactionColumns;
  const defaultFilter = tab.defaultFilter ?? defaultCustomerTransactionFilter;
  const showAllDecimals = tab.showAllDecimals ?? false;
  const setShowAllDecimals = useCallbackRef((showAllDecimals: boolean) => {
    onUpdateTab({ ...tab, showAllDecimals });
  });

  const defaultColumns = useCustomerTransactionColumns(defaultBlotterColumns);
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useCustomerTransactionsFilter({
    persistedBlotterTable,
  });

  const { filterBuilderProps } = filterResults;

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    mixpanel.track(MixpanelEvent.ChangeBlotterColumns, {
      [MixpanelEventProperty.TabLabel]: 'Customer Transfers',
    });
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialFiltersOpen },
    filterBuilderProps: filterResults.filterBuilderProps,
  });
  const openClause = useSpaceParameterOpenClause(
    filterBuilderAccordion.openClause,
    CUSTOMER_TRANSACTIONS_SPACE_PARAMETER_SUPPORT
  );

  const rxjsFilterFunc = useCallback(
    (data: CustomerTransaction) => {
      if (filterExistsAndExcludes(filterResults.filter, 'Currencies', data, 'Currency')) {
        return false;
      }

      if (filterExistsAndExcludes(filterResults.filter, 'Counterparties', data, 'Counterparty')) {
        return false;
      }

      if (filterExistsAndExcludes(filterResults.filter, 'Statuses', data, 'Status')) {
        return false;
      }

      if (filterExistsAndExcludes(filterResults.filter, 'TransactionTypes', data, 'TransactionType')) {
        return false;
      }

      return true;
    },
    [filterResults.filter]
  );
  const filterPipe = useWSFilterPipe({ getUniqueKey: getCustomerTransactionKey, filterFunc: rxjsFilterFunc });

  const { handleClickJson, jsonModal } = useJsonModal();

  const getCustomContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams) => [
      ...filterByCellValueMenuItem({
        params,
        openClause,
        filterableProperties: filterBuilderProps.properties,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      agGridMenuSeparator,
    ],
    [filterBuilderProps.properties, mixpanel, openClause]
  );

  const request = useMemo(
    () =>
      ({
        name: CUSTOMER_TRANSACTION,
        tag: blotterID,
        sort_by: '-Timestamp',
        ...removeEmptyFilters(onlyServerKeys(filterResults.filter)),
      }) satisfies WebsocketRequest,
    [blotterID, filterResults.filter]
  );

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimals ?? false,
    }),
    [showAllDecimals]
  );

  const blotterTable = useWsBlotterTable<WebsocketRequest, CustomerTransaction>({
    request,
    pipe: filterPipe,
    rowID: 'TransactionID' satisfies keyof CustomerTransaction,
    columns: defaultColumns,
    handleClickJson,
    persistence: persistedBlotterTable,
    settings: blotterTableSettings,
    contextMenuOptions: { getCustomContextMenuItems },
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      onRowDoubleClicked,
      rowSelection: DEFAULT_BLOTTER_SELECTION_SINGLE_PARAMS,
    },
    exportOptions: {
      name: 'Customer Transfers',
      tabLabel: tab.label,
    },
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        {...filterResults.blotterTableFilterProps}
        suffix={
          <>
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              {!showAllDecimalsInBlotters && (
                <Toggle
                  size={FormControlSizes.Small}
                  checked={showAllDecimals}
                  onChange={setShowAllDecimals}
                  label="Show All Decimals"
                  data-testid="show-all-decimals-toggle"
                />
              )}
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {jsonModal}
    </>
  );
};

function onlyServerKeys(filter: CustomerTransactionsFilter | undefined): CustomerTransactionsFilter {
  if (filter == null) {
    return {};
  }

  return pick(filter, ['StartDate', 'EndDate', 'TransactionTypes']);
}
